import Clouds from '@/common/Clouds';
import { SectionParagraph as Subtitle, MainHeader as Title } from '@/components/shared/Content';
import JoinCTA from '@/components/shared/JoinCTA';
import { PartnerLogo } from '@/components/shared/PartnerLogo';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import useSmoothScroll from '@/hooks/useSmoothScroll';
import house1lg from '@/static/assets/homeowners/houses/png/lg/house1comp-min.webp';
import house2lg from '@/static/assets/homeowners/houses/png/lg/house2comp-min.webp';
import house3lg from '@/static/assets/homeowners/houses/png/lg/house3comp-min.webp';
import house4lg from '@/static/assets/homeowners/houses/png/lg/house4comp-min.webp';
import house1sm from '@/static/assets/homeowners/houses/png/sm/house1-min.webp';
import house2sm from '@/static/assets/homeowners/houses/png/sm/house2-min.webp';
import house3sm from '@/static/assets/homeowners/houses/png/sm/house3-min.webp';
import house4sm from '@/static/assets/homeowners/houses/png/sm/house4-min.webp';
import smoke from '@/static/assets/homeowners/houses/smoke.gif';
import { type Product, Products } from '@/utils/products';
import { Box, Flex, easeOutQuad } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { clampedInversedLerp, lerp } from '@lemonade-hq/ts-helpers';
import { motion } from 'motion/react';
import type React from 'react';
import { memo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import House from './House';
import {
  desktopTopSectionHeight,
  desktopTopSectionMargin,
  mobileTopSectionMargin,
  tabletTopSectionHeight,
  tabletTopSectionMargin,
} from './shared/GeneralLayout';

const StyledClouds = styled(Clouds)`
  z-index: 0;
  ${MEDIA_QUERIES.mobile`
    transform: scale(2);
  `}
  ${MEDIA_QUERIES.tabletAndDesktop`
    transform: scale(1.5);
  `}
`;

const AnimatedClouds = motion.create(StyledClouds);

const Houses = styled(Flex)`
  pointer-events: none;
  position: absolute;
  top: calc(100vh - 750px);
  left: 50%;
  height: 1386px;
  width: 2576px;
  margin: auto;
  transform: translateX(-50%);
  right: auto;
  z-index: 0;

  @media (max-height: 680px) {
    top: calc(100vh - 575px);
  }

  ${MEDIA_QUERIES.tablet`
    top: calc(100vh - 650px);
  `}

  ${MEDIA_QUERIES.desktop`
    height: 1386px;
    width: 2576px;
    top: calc(100vh - 800px);
  `}

  @media (min-width: 768px) and (max-width: 1450px) and (max-height: 767px) {
    width: 2088px;
    top: calc(100vh - 345px);
  }

  @media (max-width: 1450px) and (max-height: 767px) and (min-width: 1024px) {
    width: 2088px;
    height: 1386px;
    top: calc(100vh - 545px);
  }
`;

const HouseSep = styled(Flex)`
  background: white;
  height: 693px;
  width: 100%;
  left: auto;
  right: auto;
  position: absolute;
  top: 0;

  ${MEDIA_QUERIES.tabletAndDesktop`
    height: 1386px;
    width: 2576px;
`}

  @media (min-width: 768px) and (max-width: 1450px) and (max-height: 767px) {
    width: 2088px;
  }
`;

const Smoke = styled(Box)`
  ${MEDIA_QUERIES.mobile`
        display: none;
    `}

  background-image: url(${smoke.src});
  background-repeat: no-repeat;
  background-size: 100%;
  height: 800px;
  width: 336px;
  position: absolute;
  top: -390px;
  left: 52%;
  z-index: 5;
  opacity: 0.05;
`;

const AnimatedSmoke = motion.create(Smoke);

const MainTopSection = styled(Flex)`
  pointer-events: none;
  @media (min-width: 768px) and (max-width: 1024px) {
    min-height: calc(100vh + 575px);
  }

  @media (max-width: 768px) and (min-height: 681px) and (max-height: 719px) {
    min-height: calc(100vh + 180px);
  }

  @media (max-width: 375px) and (max-height: 680px) {
    min-height: calc(100vh + 312px);
  }

  @media (min-width: 376px) and (max-width: 768px) and (max-height: 680px) {
    min-height: calc(100vh + 292px);
  }

  @media (min-height: 768px) and (min-width: 768px) {
    min-height: calc(100vh + 280px);
  }

  @media (min-height: 768px) and (max-width: 1024px) and (min-width: 768px) {
    min-height: calc(100vh + 460px);
  }

  @media (min-width: 1281px) {
    min-height: calc(100vh + 300px);
  }
`;

const StyledTitle = styled(Title)`
  white-space: normal;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: pre;
  `}
`;

const StyledSubtitle = styled(Subtitle)`
  white-space: pre-line;
  ${MEDIA_QUERIES.tabletAndDesktop`
    white-space: normal!important;
  `}
`;

const StyledPartnerLogo = styled(PartnerLogo)`
  margin-top: 0px;
  margin-bottom: 0;
  ${MEDIA_QUERIES.desktop`
    margin-top: -80px;
    margin-bottom: 40px;
  `}
`;

const Cta = styled(JoinCTA)`
  &&& {
    display: block;
    padding: 18px 44px;
    font-size: 1.8rem;
  }
`;

export interface HeroProps {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  customProduct?: Product;
  ctaUrl?: string;
  ctaLabel?: string;
  trackingLabel?: string;
  partnerLogo?: string;
  frontHouseImage?: {
    lg: string;
    sm: string;
    alt?: string;
    houseTranslateY?: {
      lg: number;
      sm: number;
    };
  };
}

const MainCta: React.FC<Pick<HeroProps, 'ctaUrl' | 'trackingLabel' | 'customProduct' | 'ctaLabel'>> = ({
  ctaUrl,
  trackingLabel,
  ctaLabel,
  customProduct,
}) => {
  const { ref: mainCtaRef } = useShowHeaderCtaOnScroll();
  const originalProduct = customProduct ?? Products.PRODUCT_HOME_US_HOMEOWNERS;
  return (
    <Cta
      size="big"
      flavor="primary"
      glowing
      ref={mainCtaRef}
      customUrl={ctaUrl}
      id="gtm_button_homeowners_check_prices"
      data-source="homeowners"
      product={originalProduct}
      analyticsParams={
        trackingLabel
          ? {
              url: ctaUrl ?? '',
              position: 'header',
              landing_page_generator: 'true',
              original_product: originalProduct,
              page_name: trackingLabel ?? '',
            }
          : {}
      }
    >
      {ctaLabel}
    </Cta>
  );
};

const Content: React.FC<
  Pick<HeroProps, 'partnerLogo' | 'title' | 'subtitle' | 'ctaUrl' | 'trackingLabel' | 'customProduct' | 'ctaLabel'>
> = ({ partnerLogo, title, subtitle, ctaUrl, trackingLabel, customProduct, ctaLabel }) => {
  return (
    <Flex flexDirection="column" alignItems="center" zIndex={0}>
      <StyledPartnerLogo src={partnerLogo} />
      <StyledTitle tabIndex={0}>{title}</StyledTitle>
      <Box mb={[40, 60]} mt={[20, 17]}>
        <StyledSubtitle tabIndex={0}>{subtitle}</StyledSubtitle>
      </Box>
      <MainCta ctaUrl={ctaUrl} customProduct={customProduct} trackingLabel={trackingLabel} ctaLabel={ctaLabel} />
    </Flex>
  );
};

const MemoizedContent = memo(Content);

export const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  customProduct,
  ctaUrl,
  trackingLabel,
  ctaLabel = 'Check our prices',
  partnerLogo,
  frontHouseImage = {
    lg: house1lg.src,
    sm: house1sm.src,
  },
}) => {
  const { smoothRelativeScroll } = useSmoothScroll();
  const housesRef = useRef<Element>(null);
  const houseLerp = useRef(0);

  const WhiteSepOpacityBeforeLayerTwo = lerp(0, 0.67, houseLerp.current);
  const WhiteSepOpacityBeforeLayerFour = lerp(0, 0.33, houseLerp.current);
  const WhiteSepOpacityBeforeLayerFIve = lerp(0, 0.1, houseLerp.current);

  const opacityStyleTwo = {
    zIndex: 5,
    opacity: WhiteSepOpacityBeforeLayerTwo,
  };
  const opacityStyleFour = {
    zIndex: 3,
    opacity: WhiteSepOpacityBeforeLayerFour,
  };
  const opacityStyleFive = {
    zIndex: 2,
    opacity: WhiteSepOpacityBeforeLayerFIve,
  };

  const SmokeTranslateY = lerp(0, -60, houseLerp.current);
  const smokeStyle = {
    transform: `translateY(${SmokeTranslateY}px)`,
  };

  useEffect(() => {
    if (!housesRef.current) return;
    houseLerp.current = easeOutQuad(clampedInversedLerp(0, 0.06, smoothRelativeScroll));
  }, [smoothRelativeScroll]);

  return (
    <Box position="relative">
      <AnimatedClouds
        animate={{
          opacity: [0, 1],
        }}
        top={[30, 500, 70]}
      />
      <Flex
        textAlign="center"
        flexDirection="column"
        alignItems="center"
        mt={[mobileTopSectionMargin + 10, tabletTopSectionMargin + 10, desktopTopSectionMargin]}
        px={[20, 20, 0]}
        mb={0}
        zIndex={1}
      >
        <MainTopSection
          position="relative"
          flexDirection="column"
          alignItems="center"
          minHeight={['calc(100vh + 190px)', 'calc(100vh + 650px)', 'calc(100vh + 390px)']}
          maxWidth={[375, 1140, 1140]}
          zIndex={3}
          height={['calc(100vh + 190px)', tabletTopSectionHeight, desktopTopSectionHeight]}
        >
          <MemoizedContent
            title={title}
            subtitle={subtitle}
            customProduct={customProduct}
            ctaUrl={ctaUrl}
            trackingLabel={trackingLabel}
            ctaLabel={ctaLabel}
            partnerLogo={partnerLogo}
          />
        </MainTopSection>
        <Houses ref={housesRef}>
          <House
            houseBg={house4lg.src}
            houseBgMobile={house4sm.src}
            houseOpacity={1}
            houseZindex={1}
            houseTop={['-145px', '-132px', '-132px']}
            houseLeft="auto"
            houseTranslateY={130}
            houseScale={1}
            houseScaleMobile={0.8}
            houseTranslateMobile={100}
            houseLerp={houseLerp.current}
            altText={frontHouseImage.alt}
          />
          <HouseSep style={opacityStyleFive} />
          <House
            houseBg={house3lg.src}
            houseBgMobile={house3sm.src}
            houseOpacity={1}
            houseZindex={2}
            houseTop={['-110px', '-135px', '-135px']}
            houseLeft="auto"
            houseTranslateY={50}
            houseScale={0.95}
            houseScaleMobile={0.8}
            houseTranslateMobile={80}
            houseLerp={houseLerp.current}
            altText={frontHouseImage.alt}
          />
          <HouseSep style={opacityStyleFour} />

          <House
            houseBg={house2lg.src}
            houseBgMobile={house2sm.src}
            houseOpacity={1}
            houseZindex={4}
            houseTop={['-50px', '-140px', '-140px']}
            houseLeft="auto"
            houseTranslateY={-10}
            houseScale={0.95}
            houseScaleMobile={0.8}
            houseTranslateMobile={60}
            houseLerp={houseLerp.current}
            altText={frontHouseImage.alt}
          />
          <HouseSep style={opacityStyleTwo} />
          <Flex>
            <AnimatedSmoke
              animate={{
                opacity: [0, 0.05],
              }}
              style={smokeStyle}
            />
            <House
              houseBg={frontHouseImage.lg}
              houseBgMobile={frontHouseImage.sm}
              houseOpacity={1}
              houseZindex={5}
              houseTop={['105px', '-145px', '-145px']}
              houseLeft="-5px"
              houseTranslateY={frontHouseImage.houseTranslateY?.lg ?? -40}
              houseScale={1}
              houseScaleMobile={0.85}
              houseTranslateMobile={frontHouseImage.houseTranslateY?.sm ?? -40}
              houseLerp={houseLerp.current}
              altText={frontHouseImage.alt}
            />
          </Flex>
        </Houses>
      </Flex>
    </Box>
  );
};
